import { API_ENDPOINTS } from '@tsgecart/config';
import { storefrontApiFetch } from '../../../../lib/api/storefront-client.js';
import type { Category, Brand, ProductListItem, ProductDetail, ProductListParams, Banner } from '../types/catalog.js';

export interface FetchOptions {
  signal?: AbortSignal;
}

/**
 * Skips `undefined`/`null` AND empty strings -- an optional filter like
 * `categoryId` that's "not selected yet" (e.g. the related-products query
 * on the product detail page, built before `product` has loaded) must
 * never be serialized as `categoryId=`, which the backend's Zod schema
 * correctly rejects as invalid (an empty string is not a valid UUID).
 * Omitting the param entirely is what "no filter" means to every other
 * caller of this function, so an empty string has to mean the same thing,
 * not "filter by the empty category."
 */
function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export async function fetchCategories(
  params: { parentId?: string; limit?: number; offset?: number } = {},
  options: FetchOptions = {},
): Promise<Category[]> {
  const query = buildQuery(params);
  return storefrontApiFetch<Category[]>(`${API_ENDPOINTS.catalogCategories}${query}`, options);
}

export async function fetchCategoryBySlug(slug: string, options: FetchOptions = {}): Promise<Category> {
  return storefrontApiFetch<Category>(`${API_ENDPOINTS.catalogCategories}/${slug}`, options);
}

export async function fetchBrands(
  params: { limit?: number; offset?: number } = {},
  options: FetchOptions = {},
): Promise<Brand[]> {
  const query = buildQuery(params);
  return storefrontApiFetch<Brand[]>(`${API_ENDPOINTS.catalogBrands}${query}`, options);
}

export async function fetchProducts(params: ProductListParams, options: FetchOptions = {}): Promise<ProductListItem[]> {
  const query = buildQuery({
    categoryId: params.categoryId,
    brandId: params.brandId,
    isFeatured: params.isFeatured,
    priceMinPaise: params.priceMinPaise,
    priceMaxPaise: params.priceMaxPaise,
    inStockOnly: params.inStockOnly,
    search: params.search,
    sortField: params.sortField,
    sortDirection: params.sortDirection,
    limit: params.limit,
    offset: params.offset,
  });
  return storefrontApiFetch<ProductListItem[]>(`${API_ENDPOINTS.catalogProducts}${query}`, options);
}

export async function fetchProductBySlug(slug: string, options: FetchOptions = {}): Promise<ProductDetail> {
  return storefrontApiFetch<ProductDetail>(`${API_ENDPOINTS.catalogProducts}/${slug}`, options);
}

export async function fetchBanners(placement: string, options: FetchOptions = {}): Promise<Banner[]> {
  return storefrontApiFetch<Banner[]>(`${API_ENDPOINTS.banners}?placement=${encodeURIComponent(placement)}`, options);
}

/**
 * Analytics beacons -- deliberately fire-and-forget. Callers never `await`
 * these before navigating on a banner click (a slow/failed analytics call
 * must never block or break the click-through), so failures are swallowed
 * here rather than surfaced as an unhandled rejection or a thrown
 * `StorefrontApiError` the caller would have to remember to catch.
 */
export function recordBannerView(bannerId: string): void {
  void storefrontApiFetch(`${API_ENDPOINTS.banners}/${bannerId}/view`, { method: 'POST' }).catch(() => {});
}

export function recordBannerClick(bannerId: string): void {
  void storefrontApiFetch(`${API_ENDPOINTS.banners}/${bannerId}/click`, { method: 'POST' }).catch(() => {});
}
